import React, { useState, useEffect } from "react";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { Button } from "@/components/ui/Button";
import TextAreaBox from "@/components/ui/TextAreaBox";
import ModalLayout from "@/components/layout/ModalLayout";
import { toast } from "sonner";
import ModalHeader from "@/components/ui/ModalHeader";
import SearchModal from "./Search";
import RadioGroup from "@/components/ui/RadioBtn";
import { useItemCodeSearch, useCreateItemCode, useNextItemCode, useSearchCasNo } from "@/hooks/standard/st_item_code/useItemCode";
import { useCodeListByItemGroupCode } from "@/hooks/standard/st_code/useCode";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ItemCodeData) => void;
}

export interface ItemCodeData {
  itemCode: string;
  useStatus: string;
  itemCategory: string;
  note: string;
  searchType: "itemName" | "casNumber";
  searchQuery: string;
  itemName: string;
  casNumber: string;
  sfsgwd: string;
  itemGroupCode: string;
  specification: string;
  unit: string;
  remark: string;
  chemId: string;
}

export default function AddModal({ isOpen, onClose, onSave }: AddModalProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchType, setSearchType] = useState<"itemName" | "casNumber">("itemName");
  const { data: bigCategoryData } = useCodeListByItemGroupCode();
  // 훅과 연결: 검색어·타입·페이지 사이즈
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const createMutation = useCreateItemCode();
  
  // 다음 품목코드 조회 (모달이 열릴 때만 활성화)
  const { data: nextItemCode, refetch: refetchNextCode } = useNextItemCode(isOpen);
  
  const [formData, setFormData] = useState<ItemCodeData>({
    itemCode: "",
    useStatus: "1",
    itemCategory: "",
    note: "",
    searchType: "itemName",
    searchQuery: "",
    itemGroupCode: "",
    itemName: "",
    casNumber: "",
    sfsgwd: "",
    specification: "",
    unit: "",
    remark: "",
    chemId: "",
  });

  // CAS 번호로 유해위험분류 조회 (mutation - 더블클릭 시에만 호출)
  const searchCasNoMutation = useSearchCasNo();

  // 모달이 열릴 때마다 다음 품목코드 자동 설정
  useEffect(() => {
    if (isOpen && nextItemCode) {
      setFormData(prev => ({
        ...prev,
        itemCode: nextItemCode
      }));
    }
  }, [isOpen, nextItemCode]);
  // 사용여부 옵션
  const useStatusOptions = [
    { value: "1", label: "사용" },
    { value: "0", label: "미사용" },
  ];

  const handleInputChange = (field: keyof ItemCodeData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // 필수 필드 검증
    if (!formData.useStatus.trim()) {
      toast.warning("사용여부를 선택해주세요.");
      return;
    }

    if (!formData.itemCategory.trim()) {
      toast.warning("품목구분을 선택해주세요.");
      return;
    }

    if (!formData.itemName.trim()) {
      toast.warning("품목명 또는 CAS 번호를 입력해주세요.");
      return;
    }

    try {
      // API 호출 - 백엔드 필드명에 맞게 변환
      await createMutation.mutateAsync({
        stItemCode: formData.itemCode,
        stItemName: formData.itemName,
        stItemCasNo: formData.casNumber,
        stItemUse: formData.useStatus,
        stItemGroupCode: formData.itemCategory,
        stItemSize: formData.specification,
        stItemUnit: formData.unit,
        stItemRemark: formData.note,
        sfsgwd: formData.sfsgwd,
        chemId: formData.chemId,
      });
      
      onSave(formData);
      handleCancel();
      onClose();
      
      // 저장 후 다음 품목코드 다시 조회
      refetchNextCode();
    } catch (error) {
      console.error("저장 실패:", error);
    }
  };

  const handleCancel = () => {
    // 폼 데이터 초기화
    setFormData({
      itemCode: "",
      useStatus: "1",
      itemCategory: "",
      note: "",
      searchType: "itemName",
      searchQuery: "",
      itemGroupCode: "",
      itemName: "",
      casNumber: "",
      sfsgwd: "",
      specification: "",
      unit: "",
      remark: "",
      chemId: "",
    });
    
    setSearchKeyword("");
    setAllData([]);
    setCurrentPage(0);
    setSearchEnabled(false);
    setIsLoadingMore(false);
    setTotalElements(0);
    setSearchType("itemName");
    
    onClose();
  };

  // 품목 검색 열기 (order Add와 동일하게 아이콘 클릭으로 열림)
  const openItemSearch = () => {
    // 검색 모달 열기 전에 현재 검색어를 설정
    if (formData.searchQuery.trim()) {
      setSearchKeyword(formData.searchQuery);
      setSearchEnabled(true);
    }
    setIsSearchOpen(true);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      openItemSearch();
    }
  };

    // 페이지 상태 추가
    const [currentPage, setCurrentPage] = useState(0);
    const [allData, setAllData] = useState<any[]>([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [totalElements, setTotalElements] = useState(0);
    
    const { data: hookData, isLoading: isSearchLoading } = useItemCodeSearch({
      search: searchKeyword,
      searchType: searchType,
      page: currentPage,
      size: 20,
      enabled: searchEnabled || isLoadingMore,
    });

    const source = hookData;
    const newRows = Array.isArray(source) ? source : (source?.content ?? []);
    
    // 데이터 누적 로직
    useEffect(() => {
      if (!hookData) return;
      // 전체 개수 갱신(가능하면 응답의 totalElements, 없으면 기존 방식 보완)
      const nextTotal =
        (hookData as any)?.totalElements ??
        (newRows.length > 0 && (newRows[0] as any)?.totalCount ? (newRows[0] as any).totalCount : 0);
  
      if (newRows.length > 0) {
        if (currentPage === 0) {
          // 첫 페이지: 교체
          setAllData(newRows);
          setTotalElements(nextTotal || 0);
        } else {
          // 다음 페이지: 누적
          setAllData(prev => [...prev, ...newRows]);
          setTotalElements(prev => (nextTotal ? nextTotal : prev));
        }
      } else if (currentPage === 0) {
        // 첫 페이지에 결과 없음
        setAllData([]);
        setTotalElements(0);
      }
  
      setSearchEnabled(false);
      setIsLoadingMore(false);
    }, [hookData, currentPage, newRows, totalElements]);
    
    // 무한스크롤을 위한 데이터
    const rows = allData;
    
    // totalCount 확인 (첫 번째 데이터에서 가져옴)
    const totalCount = rows.length > 0 && rows[0].totalCount ? rows[0].totalCount : 0;
    const hasMore = totalCount > rows.length;

  // 검색 결과 선택 → 품목명 주입 (+ 유관 필드 동반 세팅)
  const handleSearchSelect = async (item: any) => {
    const selectedCas = item.stItemCasNo || item.casNo || "";

    setFormData(prev => ({
      ...prev,
      itemName: item.stItemName || item.chemNameKor || "",
      itemCode: item.stItemCode || prev.itemCode,
      casNumber: selectedCas,
      sfsgwd: item.sfsgwd || prev.sfsgwd || "", // 기본값은 검색 결과에서
      chemId: item.chemId || prev.chemId || "",
      specification: item.stItemSize || item.specification || "",
      unit: item.stItemUnit || item.unit || "",
    }));

    // CAS 번호가 있으면 유해위험분류 조회 (더블클릭 시에만 호출)
    if (selectedCas) {
      try {
        const sfsgwd = await searchCasNoMutation.mutateAsync({ stItemCasNo: selectedCas });
        setFormData(prev => ({ ...prev, sfsgwd }));
      } catch (error) {
        // 조회 실패 시 무시 (기본값 유지)
        console.error("유해위험분류 조회 실패:", error);
      }
    }

    setIsSearchOpen(false);
  };

  // 검색 테이블 컬럼 (API 응답 데이터에 맞게 수정)
  const searchColumns = [
    { key: "chemNameKor", header: "품목명", bodyAlign: "left" as const, headerAlign: "center" as const },
    { key: "casNo", header: "cas번호", bodyAlign: "center" as const, headerAlign: "center" as const },
    { key: "sfsgwd", header: "유해위험분류", bodyAlign: "center" as const, headerAlign: "center" as const }
  ];

  return (
    <ModalLayout isOpen={isOpen} onClose={handleCancel} title="품목코드 추가">
      {/* 기본 정보 섹션 */}
      <div className="rounded-lg">
        <ModalHeader title="기본 정보" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <InputBox
              required={true}
              label="품목코드"
              value={formData.itemCode}
              onChange={e => handleInputChange("itemCode", e.target.value)}
              placeholder="자동생성"
              className="w-full"
              readOnly
            />
          </div>
          
          <div>
            <SelectBox
              label="사용여부"
              value={formData.useStatus}
              onChange={value => handleInputChange("useStatus", value)}
              options={useStatusOptions}
              placeholder="선택"
              className="w-full"
            />
          </div>
          
          <div>
            <SelectBox
              required={true}
              label="품목구분"
              value={formData.itemCategory}
              onChange={value => handleInputChange("itemCategory", value)}
              options={bigCategoryData
              ?.map((item: any) => ({
                value: item.code,
                label: item.name,
              })) || []}
              placeholder="선택"
              className="w-full"
            />
          </div>
        </div>
        {/* 비고 필드 (전체 너비) */}
        <div className="mt-4 mb-4">
          <TextAreaBox
            label="비고"
            value={formData.note}
            onChange={e => handleInputChange("note", e.target.value)}
            placeholder="입력"
            className="w-full"
            rows={1}
          />
        </div>
      </div>
      {/* 상세 정보 섹션 */}
      <div className="rounded-lg">
        <ModalHeader title="상세 정보" />
        {/* 품목 검색 구분 */}
        <div className="mb-4">
          <div className="flex gap-4">
            <RadioGroup
              required={true}
              label="품목 검색 구분"
              name="userType"
              value={searchType}
              onChange={e => setSearchType(e as "itemName" | "casNumber")}
              options={[
                { value: "itemName", label: "품목명" },
                { value: "casNumber", label: "CAS 번호" },
              ]}
            />
          </div>
        </div>

        {/* 검색 필드 (order Add와 동일 UX: 품목명 필드에 검색 아이콘) */}
        <div className="mb-4">
          <div className="space-y-4">
            <InputBox
              required
              value={formData.searchQuery}
              showIcon
              placeholder="검색"
              onIconClick={openItemSearch}
              onChange={e => handleInputChange("searchQuery", e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
        </div>

        {/* 품목 상세 정보 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              label="품목명"
              value={formData.itemName}
              onChange={e => handleInputChange("itemName", e.target.value)}
              className="w-full"
              placeholder="입력"
            />
          </div>
          
          <div>
            <InputBox
              label="CAS 번호"
              value={formData.casNumber}
              onChange={e => handleInputChange("casNumber", e.target.value)}
              className="w-full"
              readOnly
            />
          </div>
          
          <div>
            <InputBox
              label="유해위험분류"
              value={formData.sfsgwd}
              onChange={e => handleInputChange("sfsgwd", e.target.value)}
              className="w-full"
              readOnly
            />
          </div>
        </div>

        {/* 규격 및 단위 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputBox
              label="규격"
              value={formData.specification}
              onChange={e => handleInputChange("specification", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>
          
          <div>
            <InputBox
              label="단위"
              value={formData.unit}
              onChange={e => handleInputChange("unit", e.target.value)}
              placeholder="입력"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className="py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleSave} className="py-[10px]">
            저장
          </Button>
        </div>
      </div>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={
          () => setIsSearchOpen(false)
        }
        onSelect={handleSearchSelect}
        title="품목 검색"
        columns={searchColumns}
        data={rows as any[]}
        isLoadingInitial={isSearchLoading && rows.length === 0}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        searchOptions={[
          { value: "chemNameKor", label: "품목명" },
          { value: "casNo", label: "cas번호" },
          { value: "chemId", label: "화학물질 ID" },
        ]}
        onSearch={(keyword) => {
          setSearchType(searchType);
          setSearchKeyword(keyword);
          setIsLoadingMore(false);
          setCurrentPage(0);
          setAllData([]); // 데이터 초기화
          setSearchEnabled(true);
        }}
        onSearchButtonClick={(keyword) => {
          setSearchType(searchType);
          setSearchKeyword(keyword);
          setIsLoadingMore(false);
          setCurrentPage(0);
          setAllData([]); // 데이터 초기화
          setSearchEnabled(true);
        }}
        onInfiniteScroll={() => {
          // 무한스크롤 시 다음 페이지 로딩
          if (hasMore) {
            setIsLoadingMore(true);
            setCurrentPage(prev => {
              return prev + 1;
            });
          }
        }}
        initialSearchKeyword={formData.searchQuery}
      />
    </ModalLayout>
  );
}